// src/pages/Settings.tsx
'use client';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { currentUser, signOut, getSupabase } from "@/lib/supabase";
import {
  User, Shield, Settings as SettingsIcon, Bell,
  ChevronLeft, Edit3, X, Mail, Phone, Download, Smartphone,
  Check, Loader2, AlertCircle
} from 'lucide-react';

import AccountTab from "@/components/settings/AccountTab";

// ——————————————————————————————————
// TYPES
// ——————————————————————————————————
interface DevicesState {
  garmin: boolean;
  whoop: boolean;
}

interface AutomationsState {
  automations_enabled: boolean;
  wants_daily_calorie_log: boolean;
  wants_daily_recovery: boolean;
  wants_weekly_weigh_in: boolean;
  wants_weekly_review: boolean;
  wants_post_workout_nutrition: boolean;
}

interface ProfileState {
  email_address: string;
  phone: string;
}

// ——————————————————————————————————
// TOGGLE COMPONENT
// ——————————————————————————————————
function Toggle({ 
  label, 
  enabled, 
  onToggle, 
  disabled = false, 
  tooltip 
}: { 
  label: string; 
  enabled: boolean; 
  onToggle: (value: boolean) => void; 
  disabled?: boolean; 
  tooltip?: string; 
}) {
  return (
    <div className={`flex items-center justify-between py-3 border-b border-[#e2e8f0] last:border-0 ${disabled ? 'opacity-50' : ''}`}>
      <div className="flex-1 min-w-0 mr-4">
        <p className="font-medium text-sm md:text-base">{label}</p>
        {tooltip && <p className="text-[10px] md:text-xs text-yellow-400">{tooltip}</p>}
      </div>
      <button
        onClick={() => !disabled && onToggle(!enabled)}
        disabled={disabled}
        className={`w-11 md:w-12 h-6 rounded-full transition-colors relative flex-shrink-0 ${enabled ? 'bg-green-500' : 'bg-[#f1f5f9]'} ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${enabled ? 'translate-x-5 md:translate-x-6' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );
}

// ——————————————————————————————————
// ERROR TOAST COMPONENT
// ——————————————————————————————————
function ErrorToast({ message, onClose }: { message: string; onClose: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed bottom-4 right-4 left-4 md:left-auto md:w-96 bg-red-500/90 backdrop-blur-sm text-[#0f172a] px-4 py-3 rounded-xl shadow-lg flex items-center gap-3 z-50 animate-in slide-in-from-bottom">
      <AlertCircle className="w-5 h-5 flex-shrink-0" />
      <p className="text-sm flex-1">{message}</p>
      <button onClick={onClose} className="p-1 hover:bg-black/20 rounded">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// ——————————————————————————————————
// SUCCESS TOAST COMPONENT
// ——————————————————————————————————
function SuccessToast({ message, onClose }: { message: string; onClose: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed bottom-4 right-4 left-4 md:left-auto md:w-96 bg-green-500/90 backdrop-blur-sm text-[#0f172a] px-4 py-3 rounded-xl shadow-lg flex items-center gap-3 z-50 animate-in slide-in-from-bottom">
      <Check className="w-5 h-5 flex-shrink-0" />
      <p className="text-sm flex-1">{message}</p>
    </div>
  );
}

// ——————————————————————————————————
// MAIN SETTINGS PAGE
// ——————————————————————————————————
export default function Settings() {
  const [activeTab, setActiveTab] = useState('profile');
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [profile, setProfile] = useState<ProfileState>({ email_address: '', phone: '' });
  const [editingEmail, setEditingEmail] = useState(false);
  const [tempEmail, setTempEmail] = useState('');
  const [automations, setAutomations] = useState<AutomationsState>({
    automations_enabled: false,
    wants_daily_calorie_log: false,
    wants_daily_recovery: false,
    wants_weekly_weigh_in: false,
    wants_weekly_review: false,
    wants_post_workout_nutrition: false,
  });
  const [devices, setDevices] = useState<DevicesState>({
    garmin: false,
    whoop: false,
  });
  const [exporting, setExporting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const u = await currentUser();
        setUser(u);
        if (!u) {
          setIsLoading(false);
          return;
        }

        const supabase = getSupabase();
        const { data, error: fetchError } = await supabase
          .from("clients")
          .select(`
            email_address,
            phone,
            automations_enabled,
            wants_daily_calorie_log,
            wants_daily_recovery,
            wants_weekly_weigh_in,
            wants_weekly_review,
            wants_post_workout_nutrition,
            garmin_connected,
            whoop_connected
          `)
          .eq("user_id", u.id)
          .single();

        if (fetchError) {
          console.error("[settings] Failed to load settings:", fetchError);
          setError("Failed to load settings");
          setIsLoading(false);
          return;
        }

        setProfile({
          email_address: data.email_address || '',
          phone: data.phone || 'Not set',
        });
        setTempEmail(data.email_address || '');
        setAutomations({
          automations_enabled: data.automations_enabled || false,
          wants_daily_calorie_log: data.wants_daily_calorie_log || false,
          wants_daily_recovery: data.wants_daily_recovery || false,
          wants_weekly_weigh_in: data.wants_weekly_weigh_in || false,
          wants_weekly_review: data.wants_weekly_review || false,
          wants_post_workout_nutrition: data.wants_post_workout_nutrition || false,
        });
        setDevices({
          garmin: data.garmin_connected || false,
          whoop: data.whoop_connected || false,
        });

        // Handle WHOOP redirect
        const urlParams = new URLSearchParams(window.location.search);
        const whoopParam = urlParams.get("whoop");
        const terraUserId = urlParams.get("user_id");

        if (whoopParam === "connected" && terraUserId) {
          const { error: updateError } = await supabase
            .from("clients")
            .update({ whoop_connected: true })
            .eq("user_id", u.id);

          if (updateError) {
            console.error("[settings] Failed to update whoop_connected:", updateError);
            setError("Connected, but failed to save status. Please refresh.");
          } else {
            setDevices(prev => ({ ...prev, whoop: true }));
            setSuccess("WHOOP connected successfully!");
          }
          window.history.replaceState({}, "", window.location.pathname);
        }
      } catch (err) {
        console.error("[settings] Load error:", err);
        setError("Failed to load settings");
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, []);

  const updateEmail = async () => {
    const supabase = getSupabase();
    const currentUserData = await currentUser();
    if (!currentUserData) return;

    const { error: updateError } = await supabase
      .from("clients")
      .update({ email_address: tempEmail })
      .eq("user_id", currentUserData.id);

    if (updateError) {
      setError("Failed to update email");
      console.error("[settings] Email update error:", updateError);
      return;
    }

    setProfile(prev => ({ ...prev, email_address: tempEmail }));
    setEditingEmail(false);
    setSuccess("Email updated successfully");
  };

  const updateSetting = async (key: string, value: boolean) => {
    const supabase = getSupabase();
    const currentUserData = await currentUser();
    if (!currentUserData) return;

    const { error: updateError } = await supabase
      .from("clients")
      .update({ [key]: value })
      .eq("user_id", currentUserData.id);

    if (updateError) {
      setError("Failed to save setting");
      console.error("[settings] Setting update error:", updateError);
      return;
    }

    setAutomations(prev => ({ ...prev, [key]: value }));
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const disconnectDevice = async (
    device: keyof DevicesState, 
    endpoint: string, 
    body?: object
  ) => {
    if (!confirm(`Revoke ${device} access? You can reconnect anytime.`)) return;

    try {
      const { data: { session } } = await getSupabase().auth.getSession();
      
      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to disconnect");
      }

      const fieldName = `${device}_connected`;

      await getSupabase()
        .from("clients")
        .update({ [fieldName]: false })
        .eq("user_id", user.id);

      setDevices(prev => ({ ...prev, [device]: false }));
      setSuccess(`${device.charAt(0).toUpperCase() + device.slice(1)} disconnected successfully`);
    } catch (err: any) {
      console.error(`[settings] Failed to disconnect ${device}:`, err);
      setError(err.message || `Failed to disconnect ${device}`);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'wearables', label: 'Integrations', icon: Smartphone },
    { id: 'automations', label: 'Automations', icon: Bell },
    { id: 'export', label: 'Export', icon: Download },
    { id: 'account', label: 'Account', icon: Shield },
  ];

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#ffffff] text-[#0f172a] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-green-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#ffffff] text-[#0f172a]">
      {/* Error Toast */}
      {error && <ErrorToast message={error} onClose={() => setError(null)} />}
      
      {/* Success Toast */}
      {success && <SuccessToast message={success} onClose={() => setSuccess(null)} />}

      {/* Header */}
      <header className="border-b border-[#e2e8f0] px-4 md:px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-1 md:gap-2 text-gray-500 hover:text-[#0f172a] transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm hidden sm:inline">Back to Dashboard</span>
            <span className="text-sm sm:hidden">Back</span>
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-4 md:p-6">
        {/* Page Title */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2 md:gap-3">
            <SettingsIcon className="w-6 h-6 md:w-8 md:h-8 text-green-400" />
            Settings
          </h1>
          <p className="text-gray-500 mt-1 text-sm md:text-base">Manage your profile, devices, and data</p>
        </div>

        {/* Tabs - Scrollable on mobile */}
        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 mb-6 md:mb-8">
          <div className="flex gap-1 md:gap-6 border-b border-[#e2e8f0] min-w-max">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 md:gap-2 pb-3 px-2 md:px-1 text-xs md:text-sm font-medium transition-all border-b-2 -mb-px whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'text-green-400 border-green-400'
                      : 'text-gray-500 border-transparent hover:text-[#0f172a]'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-4 md:space-y-6">
          {/* PROFILE TAB */}
          {activeTab === 'profile' && (
            <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-4 md:p-6">
              <h2 className="text-base md:text-lg font-semibold mb-4">Profile</h2>
              <div className="space-y-4 md:space-y-6">
                {/* Avatar & Basic Info */}
                <div className="flex items-center gap-3 md:gap-4">
                  <div className="w-14 h-14 md:w-20 md:h-20 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center text-xl md:text-2xl font-bold flex-shrink-0">
                    {user?.email?.[0]?.toUpperCase() || "U"}
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-sm md:text-base truncate">{user?.email || "user@example.com"}</p>
                    <p className="text-xs md:text-sm text-gray-500">
                      Member since {new Date(user?.created_at || Date.now()).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                {/* Email & Phone */}
                <div className="border-t border-[#e2e8f0] pt-4 space-y-4">
                  {/* Email Row */}
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-500 text-sm">Email</span>
                    </div>
                    {editingEmail ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="email"
                          value={tempEmail}
                          onChange={(e) => setTempEmail(e.target.value)}
                          className="bg-[#f8fafc] border border-[#e2e8f0] rounded px-2 py-1.5 text-sm w-full sm:w-48 md:w-64"
                          autoFocus
                        />
                        <button onClick={updateEmail} className="text-green-400 hover:text-green-300 p-1">
                          <Check className="w-4 h-4" />
                        </button>
                        <button onClick={() => { setTempEmail(profile.email_address); setEditingEmail(false); }} className="text-gray-500 hover:text-gray-600 p-1">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm truncate">{profile.email_address || "Not set"}</span>
                        <button onClick={() => setEditingEmail(true)} className="text-gray-500 hover:text-[#0f172a] p-1">
                          <Edit3 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Phone Row */}
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-500 text-sm">Phone</span>
                    </div>
                    <span className="font-medium text-sm">{profile.phone}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* WEARABLES TAB */}
          {activeTab === 'wearables' && (
            <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-4 md:p-6">
              <h2 className="text-base md:text-lg font-semibold mb-4 md:mb-6">Manage Integrations</h2>
              <div className="space-y-3 md:space-y-6">
                {/* GARMIN */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 md:p-6 bg-[#f8fafc]/50 rounded-xl border border-[#e2e8f0]">
                  <div className="flex items-center gap-3 md:gap-5">
                    <div className="w-10 h-10 md:w-14 md:h-14 rounded-xl overflow-hidden bg-white shadow-lg flex-shrink-0">
                      <img
                        src="https://usbamprqshmoalvlkjch.supabase.co/storage/v1/object/public/branding/Garmin_Connect_app_1024x1024-02.png"
                        alt="Garmin"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-[#0f172a] text-sm md:text-lg">Garmin Connect</p>
                      <p className="text-xs md:text-sm text-gray-500">Sync workouts, heart rate, recovery</p>
                      {devices.garmin && <p className="text-xs text-green-400 mt-0.5">Connected</p>}
                    </div>
                  </div>

                  {devices.garmin ? (
                    <button
                      onClick={() => disconnectDevice(
                        'garmin',
                        "https://usbamprqshmoalvlkjch.supabase.co/functions/v1/revoke-garmin"
                      )}
                      className="w-full sm:w-auto px-4 md:px-8 py-2.5 md:py-3 bg-red-500/20 hover:bg-red-500/30 text-red-400 text-sm md:text-base font-medium rounded-lg border border-red-500/30 transition-all active:scale-95"
                    >
                      Disconnect
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        const phone = encodeURIComponent(profile.phone.replace(/\s/g, ''));
                        window.location.href = `https://usbamprqshmoalvlkjch.supabase.co/functions/v1/garmin-auth?phone=${phone}`;
                      }}
                      className="w-full sm:w-auto px-4 md:px-8 py-2.5 md:py-3 bg-[#00AEEF] hover:bg-[#0099D6] text-[#0f172a] text-sm md:text-base font-bold rounded-lg transition-all active:scale-95 shadow-lg shadow-[#00AEEF]/40"
                    >
                      Connect Garmin
                    </button>
                  )}
                </div>

                {/* WHOOP */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 md:p-6 bg-[#f8fafc]/50 rounded-xl border border-[#e2e8f0]">
                  <div className="flex items-center gap-3 md:gap-5">
                    <div className="w-10 h-10 md:w-14 md:h-14 rounded-xl overflow-hidden bg-white shadow-lg flex items-center justify-center flex-shrink-0">
                      <img
                        src="https://usbamprqshmoalvlkjch.supabase.co/storage/v1/object/public/branding/WHOOPWHITELOGO2.png"
                        alt="WHOOP"
                        className="h-6 md:h-10 w-auto object-contain"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-[#0f172a] text-sm md:text-lg">WHOOP</p>
                      <p className="text-xs md:text-sm text-gray-500">Sync strain, recovery, sleep, HRV</p>
                      {devices.whoop && <p className="text-xs text-green-400 mt-0.5">Connected</p>}
                    </div>
                  </div>

                  {devices.whoop ? (
                    <button
                      onClick={() => disconnectDevice(
                        'whoop',
                        "https://usbamprqshmoalvlkjch.supabase.co/functions/v1/terra-revoke"
                      )}
                      className="w-full sm:w-auto px-4 md:px-8 py-2.5 md:py-3 bg-red-500/20 hover:bg-red-500/30 text-red-400 text-sm md:text-base font-medium rounded-lg border border-red-500/30 transition-all active:scale-95"
                    >
                      Disconnect
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        const phone = encodeURIComponent(profile.phone.replace(/\s/g, ''));
                        const successRedirect = encodeURIComponent("https://www.inputperformanceai.com/settings?whoop=connected");
                        window.location.href = `https://usbamprqshmoalvlkjch.supabase.co/functions/v1/whoop-auth?phone=${phone}&success_redirect=${successRedirect}`;
                      }}
                      className="w-full sm:w-auto px-4 md:px-8 py-2.5 md:py-3 bg-[#a78bfa] hover:bg-[#8b5cf6] text-[#0f172a] text-sm md:text-base font-bold rounded-lg transition-all active:scale-95 shadow-lg shadow-[#a78bfa]/40"
                    >
                      Connect WHOOP
                    </button>
                  )}
                </div>

                {/* Privacy Note */}
                <div className="mt-4 md:mt-8 p-4 md:p-6 bg-blue-900/20 border border-blue-800 rounded-xl text-center">
                  <p className="text-xs md:text-sm text-blue-300">
                    <strong>Privacy First:</strong> You can revoke access to any wearable at any time. We never store your login credentials.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* AUTOMATIONS TAB */}
          {activeTab === 'automations' && (
            <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-4 md:p-6">
              <h2 className="text-base md:text-lg font-semibold mb-4 md:mb-6">Automations</h2>
              <p className="text-xs md:text-sm text-gray-500 mb-4">Enable automated insights and reminders</p>
              <div className="space-y-2 md:space-y-4">
                <Toggle 
                  label="Daily Calorie Log" 
                  enabled={automations.wants_daily_calorie_log} 
                  onToggle={(v) => updateSetting('wants_daily_calorie_log', v)} 
                />
                <Toggle 
                  label="Daily Recovery" 
                  enabled={automations.wants_daily_recovery} 
                  onToggle={(v) => updateSetting('wants_daily_recovery', v)} 
                  disabled={!devices.garmin && !devices.whoop} 
                  tooltip={!devices.garmin && !devices.whoop ? "Connect a device to enable" : undefined} 
                />
                <Toggle 
                  label="Weekly Weigh-in" 
                  enabled={automations.wants_weekly_weigh_in} 
                  onToggle={(v) => updateSetting('wants_weekly_weigh_in', v)} 
                />
                <Toggle 
                  label="Weekly Review" 
                  enabled={automations.wants_weekly_review} 
                  onToggle={(v) => updateSetting('wants_weekly_review', v)} 
                />
                <Toggle 
                  label="Post-workout Nutrition" 
                  enabled={automations.wants_post_workout_nutrition} 
                  onToggle={(v) => updateSetting('wants_post_workout_nutrition', v)} 
                  disabled={!devices.garmin && !devices.whoop} 
                  tooltip={!devices.garmin && !devices.whoop ? "Connect a device to enable" : undefined} 
                />
              </div>
            </div>
          )}

          {/* DATA EXPORT TAB */}
          {activeTab === 'export' && (
            <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-4 md:p-8">
              <h2 className="text-xl md:text-2xl font-bold text-[#0f172a] mb-4 md:mb-6">Export Your Data</h2>

              <p className="text-gray-500 mb-6 md:mb-8 text-sm md:text-base leading-relaxed">
                Download all of your data in CSV format. This includes every message, workout, meal plan, goal, and more.
                <br />
                <span className="text-xs md:text-sm text-gray-500">Your export is private and deleted after 24 hours.</span>
              </p>

              {/* Data Types Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-6 mb-6 md:mb-10">
                {[
                  { name: "Messages", desc: "All your chats with your AI coach" },
                  { name: "Training Log", desc: "Every workout you've logged" },
                  { name: "Meal Plan Log", desc: "All meals and nutrition data" },
                  { name: "AI Advice Log", desc: "Every recommendation from your coach" },
                  { name: "Daily Macros", desc: "Complete nutrition tracking history" },
                  { name: "Goals & Progress", desc: "All goals and weekly check-ins" },
                  { name: "Weigh-Ins", desc: "Your weight history" },
                  { name: "Training Profile", desc: "Your sport, experience, preferences" },
                  { name: "Profile Info", desc: "Name, age, height, gender, phone" },
                  { name: "Wearable Data", desc: "Heart rate, sleep, strain, recovery" },
                ].map((item) => (
                  <div key={item.name} className="flex items-start gap-3 md:gap-4 p-3 md:p-5 bg-[#f1f5f9]/50 rounded-xl border border-[#e2e8f0] hover:bg-[#f1f5f9]/80 transition">
                    <div className="w-8 h-8 md:w-10 md:h-10 bg-[#a78bfa]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 md:w-6 md:h-6 text-[#a78bfa]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-semibold text-[#0f172a] text-sm md:text-base">{item.name}</h4>
                      <p className="text-xs md:text-sm text-gray-500 truncate">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Full Export Button */}
              <div className="flex justify-center mb-6 md:mb-10">
                <button
                  type="button"
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setExporting("full");
                    try {
                      const supabase = getSupabase();
                      const { data: { session } } = await supabase.auth.getSession();
                      if (!session?.access_token) throw new Error("Not logged in");

                      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/export-user-data`, {
                        method: "POST",
                        headers: {
                          Authorization: `Bearer ${session.access_token}`,
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                          phone: profile.phone,
                          action: "export_full_account",
                        }),
                      });

                      if (!res.ok) throw new Error("Export failed");
                      const blob = await res.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `input-performance-full-export-${new Date().toISOString().split("T")[0]}.zip`;
                      a.click();
                      URL.revokeObjectURL(url);
                      setSuccess("Export downloaded successfully!");
                    } catch (err: any) {
                      setError(err.message || "Export failed");
                    } finally {
                      setExporting(null);
                    }
                  }}
                  disabled={exporting === "full"}
                  className="w-full sm:w-auto px-8 md:px-12 py-4 md:py-5 bg-[#a78bfa] hover:bg-[#8b5cf6] text-[#0f172a] font-bold rounded-2xl text-sm md:text-lg transition-all shadow-lg shadow-[#a78bfa]/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {exporting === "full" ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Exporting...
                    </>
                  ) : (
                    "Download Full Export (ZIP)"
                  )}
                </button>
              </div>

              {/* Individual Wearable Exports */}
              {(devices.garmin || devices.whoop) && (
                <div className="border-t border-gray-800 pt-6 md:pt-8">
                  <h3 className="text-lg md:text-xl font-bold text-[#0f172a] mb-4 md:mb-6 text-center">Export Wearable Data Only</h3>

                  <div className="space-y-3 md:space-y-4 max-w-md mx-auto">
                    {devices.garmin && (
                      <button
                        onClick={async () => {
                          setExporting("garmin");
                          try {
                            const { data: { session } } = await getSupabase().auth.getSession();
                            const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/export-garmin-data`, {
                              method: "POST",
                              headers: { Authorization: `Bearer ${session?.access_token}` },
                            });
                            if (!res.ok) throw new Error("Garmin export failed");
                            const blob = await res.blob();
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `garmin-data-${new Date().toISOString().split("T")[0]}.csv`;
                            a.click();
                            URL.revokeObjectURL(url);
                            setSuccess("Garmin data exported!");
                          } catch {
                            setError("Failed to export Garmin data");
                          } finally {
                            setExporting(null);
                          }
                        }}
                        disabled={exporting === "garmin"}
                        className="w-full py-3 md:py-4 bg-[#00AEEF] hover:bg-[#0099D6] text-[#0f172a] text-sm md:text-base font-bold rounded-xl transition-all shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {exporting === "garmin" ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Exporting Garmin...
                          </>
                        ) : (
                          "Export my Garmin Data"
                        )}
                      </button>
                    )}

                    {devices.whoop && (
                      <button
                        onClick={async () => {
                          setExporting("whoop");
                          try {
                            const { data: { session } } = await getSupabase().auth.getSession();
                            const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/export-whoop-data`, {
                              method: "POST",
                              headers: { Authorization: `Bearer ${session?.access_token}` },
                            });
                            if (!res.ok) throw new Error("WHOOP export failed");
                            const blob = await res.blob();
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `whoop-data-${new Date().toISOString().split("T")[0]}.csv`;
                            a.click();
                            URL.revokeObjectURL(url);
                            setSuccess("WHOOP data exported!");
                          } catch {
                            setError("Failed to export WHOOP data");
                          } finally {
                            setExporting(null);
                          }
                        }}
                        disabled={exporting === "whoop"}
                        className="w-full py-3 md:py-4 bg-[#a78bfa] hover:bg-[#8b5cf6] text-[#0f172a] text-sm md:text-base font-bold rounded-xl transition-all shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {exporting === "whoop" ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Exporting WHOOP...
                          </>
                        ) : (
                          "Export my WHOOP Data"
                        )}
                      </button>
                    )}
                  </div>
                </div>
              )}

              <p className="text-[10px] md:text-xs text-gray-500 mt-6 md:mt-10 text-center">
                You'll receive a secure download link via email (expires in 24 hours)
              </p>
            </div>
          )}

          {/* ACCOUNT TAB */}
          {activeTab === 'account' && <AccountTab onLogout={handleLogout} />}
        </div>
      </div>
    </div>
  );
}
